import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { FC } from 'react';

import { ProposalQuery } from 'generated/graphql';
import { formatCents, PLUS_TAX_TEXT } from 'utilities/money';

import ProposalLineItems from './ProposalLineItems';

type ProposalHotelFromQuery =
  ProposalQuery['proposal']['proposalOptions'][0]['hotels'][0];

interface ProposalHotelCardProps {
  hotel: ProposalHotelFromQuery;
  hotelIndex: number;
  totalNights: number;
  selectedLineItems: Record<string, boolean>;
  onLineItemToggle: (itemId: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

interface RoomConfiguration {
  roomBedType?: string | null;
  roomAmount?: number | null;
  avgPrice?: number | null;
  cancellationDeadline?: string | null;
}

const GuestRoomBeds = ({
  room,
  totalNights,
}: {
  room: RoomConfiguration;
  totalNights: number;
}) => {
  if (!room.roomAmount || room.roomAmount === 0) return null;

  const price = room.avgPrice || 0;
  const totalPrice = price * totalNights * room.roomAmount;

  const parsedDeadline = room.cancellationDeadline
    ? parseISO(room.cancellationDeadline)
    : null;
  const formattedDeadlineTime = parsedDeadline
    ? format(parsedDeadline, 'h:mm a')
    : '';
  const formattedDeadlineDate = parsedDeadline
    ? format(parsedDeadline, 'MMM do, yyyy')
    : '';

  return (
    <div className="rounded-xl border border-gray-200/50 bg-white p-4 transition-all duration-200 hover:bg-white hover:shadow-md sm:p-6">
      <div className="space-y-4 sm:mb-4 sm:flex sm:items-start sm:justify-between sm:space-y-0">
        <div className="flex-1">
          <h4 className="mb-2 font-oswald text-lg font-bold text-gray-900 sm:text-xl">
            {room.roomAmount}x {room.roomBedType} Bed Room
          </h4>
        </div>

        <div className="sm:ml-6 sm:shrink-0 sm:text-right">
          <div className="mb-2">
            <div className="flex items-baseline space-x-1 sm:justify-end">
              <span className="font-oswald text-xl font-bold text-[#72AD17] sm:text-2xl">
                {formatCents(price)}
              </span>
              <span className="font-arimo text-xs text-gray-500 sm:text-sm">
                per night
              </span>
            </div>
          </div>
          <div className="font-arimo text-sm leading-relaxed text-gray-600">
            <div className="flex items-center justify-between sm:block sm:text-right">
              <span className="text-gray-500 sm:hidden">Total:</span>
              <span className="font-semibold text-gray-900">
                {formatCents(totalPrice)} {PLUS_TAX_TEXT}
              </span>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              {totalNights} nights × {room.roomAmount} rooms
            </div>
          </div>
        </div>
      </div>

      {parsedDeadline && (
        <div className="flex items-start space-x-2 border-t border-gray-200/50 pt-4">
          <svg
            className="mt-0.5 size-4 shrink-0 text-amber-500"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <div className="font-arimo text-sm leading-relaxed text-gray-700">
            <span className="font-semibold">
              Free cancellation until {formattedDeadlineTime}
            </span>
            <span className="text-gray-500"> on {formattedDeadlineDate}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const ProposalHotelCard: FC<ProposalHotelCardProps> = ({
  hotel,
  hotelIndex,
  totalNights,
  selectedLineItems,
  onLineItemToggle,
  disabled = false,
  readOnly = false,
}) => {
  const hotelDetails = hotel.hotel;
  const hotelName = 'name' in hotelDetails ? hotelDetails.name : 'Hotel';
  const hotelAddress =
    'formattedAddress' in hotelDetails && hotelDetails.formattedAddress
      ? hotelDetails.formattedAddress
      : '';
  return (
    <div>
      {hotelIndex > 0 && (
        <div className="mb-4 text-sm font-medium text-gray-500">
          Hotel {hotelIndex + 1}
        </div>
      )}

      {/* Hotel Header */}
      <div className="mb-6">
        <div className="flex items-start space-x-3 sm:space-x-4">
          <div className="shrink-0 rounded-xl bg-emerald-50 p-2.5 transition-colors duration-200 sm:p-3">
            <BuildingOffice2Icon className="size-5 text-accentGreen sm:size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="mb-1 font-oswald text-lg font-bold capitalize leading-tight text-gray-900 sm:mb-2 sm:text-xl lg:text-2xl">
              {hotelName.toLowerCase()}
            </h3>
            {hotelAddress && (
              <p className="mb-2 font-arimo text-sm leading-relaxed text-gray-600 sm:mb-3 sm:text-base">
                {hotelAddress}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Room Details */}
      <div className="space-y-4 sm:space-y-6">
        {hotel.single && (
          <GuestRoomBeds room={hotel.single} totalNights={totalNights} />
        )}
        {hotel.double && (
          <GuestRoomBeds room={hotel.double} totalNights={totalNights} />
        )}
        {hotel.suite && (
          <GuestRoomBeds room={hotel.suite} totalNights={totalNights} />
        )}
      </div>

      {/* Line Items */}
      {hotel.lineItems.length > 0 && (
        <div className="mt-6">
          <ProposalLineItems
            lineItems={hotel.lineItems}
            selectedLineItems={selectedLineItems}
            onLineItemToggle={onLineItemToggle}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
      )}
    </div>
  );
};

export default ProposalHotelCard;
